import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/views/auth/shared/auth.service';

export interface ModuleCard {
  title: string;
  icon: string;
  routerLink: string;
  action: string;
  role: string;
  description: string;
  color: string;
  onlyInCards?: boolean;
}

interface ModuleDefinition {
  role: string;
  text: string;
  routerLink?: string;
  action?: string;
  onlyInCards?: boolean;
}

/**
 * Fonte única de verdade dos módulos exibidos como cards na home e no
 * campo de busca da navbar.
 */
@Injectable({ providedIn: 'root' })
export class ModuleCatalogService {
  private readonly definitions: ModuleDefinition[] = [
    { role: 'MENU_PEOPLES_SUBMENU_CUSTOMERS', text: 'CLIENTES', routerLink: 'customers' },
    { role: 'MENU_PEOPLES_SUBMENU_PARTNERS', text: 'PARCEIROS', routerLink: 'partners' },
    { role: 'MENU_FINANCIAL_SUBMENU_RECEIPTS', text: 'CONTAS A RECEBER', routerLink: 'accounts-receivable' },
    { role: 'MENU_FINANCIAL_SUBMENU_PASS_ON', text: 'REPASSE', routerLink: 'pass-on' },
    { role: 'MENU_FINANCIAL_SUBMENU_BILLS', text: 'CONTAS A PAGAR', routerLink: 'accounts-payable' },
    { role: 'MENU_PLANS', text: 'PLANOS', routerLink: 'plans' },
    { role: 'MENU_FORMS_SUBMENU_BRIEFING_FORMS', text: 'BRIEFING', routerLink: 'briefing' },
    { role: 'MENU_CONTRACTS', text: 'CONTRATOS', routerLink: 'contracts', onlyInCards: true },
    { role: 'MENU_DOWNLOADS', text: 'SOLUÇÕES', routerLink: 'downloads' },
    { role: 'MENU_MARKETING', text: 'MARKETING', routerLink: 'marketings' },
    { role: 'MENU_TRAININGS', text: 'ALFASU ACADEMY', routerLink: 'trainings' },
    { role: 'MENU_OS', text: 'OS', routerLink: 'service-orders' },
    { role: 'MENU_CRM_SUBMENU_SCHEDULE', text: 'AGENDA', routerLink: 'schedule' },
    { role: 'MENU_CRM_SUBMENU_CONTACTS', text: 'CONTATOS', routerLink: 'contacts' },
    { role: 'MENU_FINANCIAL_SUBMENU_CHARGES', text: 'COBRANÇAS', routerLink: 'my-account', onlyInCards: true },
    { role: 'MENU_NOTIFICATIONS', text: 'NOTIFICAÇÕES', action: 'openNotificationsModal', onlyInCards: true },
    { role: 'MENU_FORMS_SUBMENU_EVENTS_REGISTRATION', text: 'EVENTOS', routerLink: 'events' },
    { role: 'MENU_BIRTHDAYS', text: 'ANIVERSÁRIOS', routerLink: 'dashboard', onlyInCards: true },
  ];

  private readonly iconMap: { [key: string]: string } = {
    MENU_DASHBOARD: 'dashboard',
    MENU_PEOPLES_SUBMENU_CUSTOMERS: 'supervisor_account',
    MENU_PEOPLES_SUBMENU_PARTNERS: 'handshake',
    MENU_FINANCIAL_SUBMENU_RECEIPTS: 'payments',
    MENU_FINANCIAL_SUBMENU_PASS_ON: 'swap_horiz',
    MENU_FINANCIAL_SUBMENU_BILLS: 'account_balance_wallet',
    MENU_PLANS: 'loyalty',
    MENU_FORMS_SUBMENU_BRIEFING_FORMS: 'assignment',
    MENU_FORMS_SUBMENU_EVENTS_REGISTRATION: 'event_available',
    MENU_DOWNLOADS: 'cloud_download',
    MENU_MARKETING: 'campaign',
    MENU_TRAININGS: 'school',
    MENU_OS: 'build',
    MENU_CRM_SUBMENU_SCHEDULE: 'event',
    MENU_CRM_SUBMENU_CONTACTS: 'contacts',
    MENU_NOTIFICATIONS: 'notifications',
    MENU_CONTRACTS: 'description',
    MENU_BIRTHDAYS: 'cake',
    MENU_SETTINGS: 'settings',
    MENU_FINANCIAL_SUBMENU_CHARGES: 'attach_money',
    MENU_FINANCIAL_SUBMENU_REPORTS: 'insights',
  };

  private readonly descriptionMap: { [key: string]: string } = {
    MENU_DASHBOARD: 'Acesse o painel principal com indicadores e métricas',
    MENU_PEOPLES_SUBMENU_CUSTOMERS: 'Gerencie o cadastro de clientes',
    MENU_PEOPLES_SUBMENU_PARTNERS: 'Gerencie o cadastro de parceiros',
    MENU_FINANCIAL_SUBMENU_RECEIPTS: 'Visualize e gerencie contas a receber',
    MENU_FINANCIAL_SUBMENU_PASS_ON: 'Gerencie os repasses de comissões',
    MENU_FINANCIAL_SUBMENU_BILLS: 'Visualize e gerencie contas a pagar',
    MENU_FINANCIAL_SUBMENU_REPORTS: 'Acesse relatórios financeiros consolidados',
    MENU_PLANS: 'Visualize e gerencie planos',
    MENU_FORMS_SUBMENU_BRIEFING_FORMS: 'Gerencie formulários de briefing',
    MENU_FORMS_SUBMENU_EVENTS_REGISTRATION: 'Gerencie cadastros de eventos',
    MENU_DOWNLOADS: 'Acesse downloads de soluções',
    MENU_MARKETING: 'Acesse materiais de marketing',
    MENU_TRAININGS: 'Acesse treinamentos disponíveis',
    MENU_OS: 'Gerencie ordens de serviço',
    MENU_CRM_SUBMENU_SCHEDULE: 'Gerencie sua agenda de compromissos',
    MENU_CRM_SUBMENU_CONTACTS: 'Gerencie seus contatos',
    MENU_NOTIFICATIONS: 'Visualize suas notificações',
    MENU_CONTRACTS: 'Gerencie seus contratos',
    MENU_BIRTHDAYS: 'Visualize aniversários',
    MENU_SETTINGS: 'Configure o sistema',
    MENU_FINANCIAL_SUBMENU_CHARGES: 'Gerencie suas cobranças',
  };

  private readonly colorMap: { [key: string]: string } = {
    MENU_DASHBOARD: '#1976d2',
    MENU_PEOPLES_SUBMENU_CUSTOMERS: '#2196f3',
    MENU_PEOPLES_SUBMENU_PARTNERS: '#03a9f4',
    MENU_FINANCIAL_SUBMENU_RECEIPTS: '#00bcd4',
    MENU_FINANCIAL_SUBMENU_PASS_ON: '#009688',
    MENU_FINANCIAL_SUBMENU_BILLS: '#4caf50',
    MENU_FINANCIAL_SUBMENU_REPORTS: '#26a69a',
    MENU_PLANS: '#8bc34a',
    MENU_FORMS_SUBMENU_BRIEFING_FORMS: '#cddc39',
    MENU_FORMS_SUBMENU_EVENTS_REGISTRATION: '#ffeb3b',
    MENU_DOWNLOADS: '#ffc107',
    MENU_MARKETING: '#ff9800',
    MENU_TRAININGS: '#ff5722',
    MENU_OS: '#f44336',
    MENU_CRM_SUBMENU_SCHEDULE: '#e91e63',
    MENU_CRM_SUBMENU_CONTACTS: '#9575cd',
    MENU_NOTIFICATIONS: '#9c27b0',
    MENU_CONTRACTS: '#673ab7',
    MENU_BIRTHDAYS: '#3f51b5',
    MENU_SETTINGS: '#607d8b',
    MENU_FINANCIAL_SUBMENU_CHARGES: '#32CD32',
  };

  private readonly soonRoles = new Set<string>(['MENU_SETTINGS']);

  constructor(private authService: AuthService) {}

  /**
   * Retorna todos os cards de módulos configurados (sem filtro de permissão).
   */
  public getAllCards(): ModuleCard[] {
    return this.definitions.map(def => this.buildCard(def));
  }

  /**
   * Retorna apenas os cards visíveis para o usuário atual.
   */
  public getVisibleCards(): ModuleCard[] {
    const user = this.authService.getCurrentUser();
    const isAdmin = user && user.role === 'DEVELOPER';

    return this.getAllCards().filter(card => {
      if (isAdmin) {
        return true;
      }

      const hasCardShow = this.authService.isShow({ role: `${card.role}_CARDS_SHOW` });
      if (hasCardShow) {
        return true;
      }

      return this.authService.isShow({ role: `${card.role}_SHOW` }) !== false;
    });
  }

  /**
   * Retorna cards visíveis e efetivamente navegáveis (permissão de ENABLE
   * satisfeita e não marcados como "EM BREVE"). Útil para a busca da navbar.
   */
  public getNavigableCards(): ModuleCard[] {
    return this.getVisibleCards().filter(card => {
      if (this.soonRoles.has(card.role)) {
        return false;
      }
      return this.canEnableCard(card.role);
    });
  }

  public canEnableCard(role: string): boolean {
    const user = this.authService.getCurrentUser();
    const isAdmin = user && user.role === 'DEVELOPER';
    if (isAdmin) {
      return true;
    }

    const hasCardEnable = this.authService.isEnable({ role: `${role}_CARDS_ENABLE` });
    if (hasCardEnable) {
      return true;
    }

    return this.authService.isEnable({ role: `${role}_ENABLE` }) !== false;
  }

  public shouldShowSoonBadge(role: string): boolean {
    return this.soonRoles.has(role);
  }

  public normalizeSearch(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  public filter(cards: ModuleCard[], term: string): ModuleCard[] {
    const search = this.normalizeSearch(term);
    if (!search) {
      return cards;
    }
    return cards.filter(card => this.normalizeSearch(card.title).includes(search));
  }

  private buildCard(def: ModuleDefinition): ModuleCard {
    return {
      title: def.text,
      icon: this.iconMap[def.role] || 'extension',
      routerLink: def.routerLink || '',
      action: def.action || '',
      role: def.role,
      description: this.descriptionMap[def.role] || 'Acesse esta funcionalidade',
      color: this.colorMap[def.role] || '#9e9e9e',
      onlyInCards: def.onlyInCards,
    };
  }
}
